import React, { useState } from 'react';
import { useDrag } from 'react-dnd';
import { MoreHorizontal, MapPin, Calendar, MessageSquare, ExternalLink, CheckSquare, Square, Award, Clock } from 'lucide-react';

/**
 * CandidateCard - Enhanced candidate card with modern HR SaaS styling
 * Features: Drag & drop, selection, actions, score display, modern design
 */
const CandidateCard = ({
  candidate,
  onClick,
  onScheduleInterview,
  onAddNote,
  isSelected = false,
  onSelect,
  stage,
  index = 0
}) => {
  const [showActions, setShowActions] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  // Drag configuration
  const [{ isDragging }, drag] = useDrag(() => ({
    type: 'candidate',
    item: { id: candidate?.id, currentStage: stage },
    collect: (monitor) => ({
      isDragging: monitor.isDragging()
    })
  }), [candidate?.id, stage]);

  // Score color coding
  const getScoreColor = (score) => {
    if (!score) return 'text-gray-500';
    if (score >= 90) return 'text-emerald-600';
    if (score >= 75) return 'text-blue-600';
    if (score >= 60) return 'text-amber-600';
    return 'text-red-600';
  };

  const getScoreBg = (score) => {
    if (!score) return 'bg-gray-100';
    if (score >= 90) return 'bg-emerald-100';
    if (score >= 75) return 'bg-blue-100';
    if (score >= 60) return 'bg-amber-100';
    return 'bg-red-100';
  };

  // Time ago calculation
  const getTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));
    const diffInDays = Math.floor(diffInHours / 24);
    
    if (diffInDays > 0) return `${diffInDays}d ago`;
    if (diffInHours > 0) return `${diffInHours}h ago`;
    return 'Just now';
  };

  const handleSelect = (e) => {
    e?.stopPropagation();
    onSelect?.(!isSelected);
  };

  const handleCardClick = (e) => {
    // Don't trigger if clicking on interactive elements
    if (e?.target?.closest('button') || e?.target?.closest('input')) return;
    onClick?.(candidate);
  };

  return (
    <div
      ref={drag}
      className={`relative group bg-white/80 backdrop-blur-sm border border-white/60 rounded-2xl p-4 transition-all duration-200 hover:shadow-lg hover:shadow-gray-200/50 cursor-pointer select-none ${
        isDragging ? 'opacity-50 rotate-2 scale-95' : 'hover:scale-[1.02]'
      } ${
        isSelected ? 'ring-2 ring-violet-400 ring-opacity-60 bg-violet-50/60' : ''
      }`}
      onClick={handleCardClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => {
        setIsHovered(false);
        setShowActions(false);
      }}
    >
      {/* Selection Checkbox & Actions */}
      <div className="flex items-start justify-between mb-3">
        <button
          onClick={handleSelect}
          className="opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-gray-100 rounded-lg"
        >
          {isSelected ? (
            <CheckSquare className="w-4 h-4 text-violet-600" />
          ) : (
            <Square className="w-4 h-4 text-gray-400" />
          )}
        </button>
        
        <div className="relative">
          <button
            onClick={(e) => {
              e?.stopPropagation();
              setShowActions(!showActions);
            }}
            className="opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-gray-100 rounded-lg"
          >
            <MoreHorizontal className="w-4 h-4 text-gray-400" />
          </button>

          {/* Actions Dropdown */}
          {showActions && (
            <div className="absolute top-full right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg z-20 min-w-40">
              <div className="py-2">
                <button
                  onClick={(e) => {
                    e?.stopPropagation();
                    onScheduleInterview?.();
                    setShowActions(false);
                  }}
                  className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                >
                  <Calendar className="w-4 h-4" />
                  Schedule Interview
                </button>
                <button
                  onClick={(e) => {
                    e?.stopPropagation();
                    onAddNote?.();
                    setShowActions(false);
                  }}
                  className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                >
                  <MessageSquare className="w-4 h-4" />
                  Add Note
                </button>
                <button
                  onClick={(e) => {
                    e?.stopPropagation();
                    onClick?.(candidate);
                    setShowActions(false);
                  }}
                  className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                >
                  <ExternalLink className="w-4 h-4" />
                  View Profile
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      {/* Candidate Info */}
      <div className="flex items-start gap-3">
        <div className="relative">
          <img
            src={candidate?.avatar}
            alt={candidate?.avatarAlt}
            className="w-12 h-12 rounded-xl object-cover border-2 border-white shadow-md"
          />
          {/* Online status indicator if available */}
          {candidate?.isOnline && (
            <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-emerald-500 border-2 border-white rounded-full"></div>
          )}
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between">
            <div className="flex-1 min-w-0">
              <h4 className="font-semibold text-gray-900 text-sm truncate mb-1">
                {candidate?.name}
              </h4>
              
              <p className="text-xs text-gray-600 truncate mb-1">
                {candidate?.email}
              </p>
              
              {candidate?.location && (
                <div className="flex items-center gap-1 text-xs text-gray-500 mb-2">
                  <MapPin className="w-3 h-3" />
                  <span className="truncate">{candidate?.location}</span>
                </div>
              )}
            </div>
            
            {/* Score Badge */}
            {candidate?.score && (
              <div className={`${getScoreBg(candidate?.score)} ${getScoreColor(candidate?.score)} px-2 py-1 rounded-lg flex items-center gap-1 flex-shrink-0 ml-2`}>
                <Award className="w-3 h-3" />
                <span className="text-xs font-semibold">{candidate?.score}%</span>
              </div>
            )}
          </div>
          
          {/* Source & Time */}
          <div className="flex items-center justify-between text-xs text-gray-500 mt-2">
            <span className="bg-gray-100 px-2 py-1 rounded-md">
              {candidate?.source}
            </span>
            
            <div className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              <span>{getTimeAgo(candidate?.appliedDate)}</span>
            </div>
          </div>
        </div>
      </div>
      {/* Hover Actions */}
      {isHovered && !isDragging && (
        <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent rounded-2xl pointer-events-none">
          <div className="absolute bottom-3 right-3 flex gap-2 pointer-events-auto">
            <button
              onClick={(e) => {
                e?.stopPropagation();
                onScheduleInterview?.();
              }}
              className="p-2 bg-white/90 hover:bg-white border border-white/60 rounded-xl shadow-md transition-all"
              title="Schedule Interview"
            >
              <Calendar className="w-4 h-4 text-gray-700" />
            </button>
            
            <button
              onClick={(e) => {
                e?.stopPropagation();
                onAddNote?.();
              }}
              className="p-2 bg-white/90 hover:bg-white border border-white/60 rounded-xl shadow-md transition-all"
              title="Add Note"
            >
              <MessageSquare className="w-4 h-4 text-gray-700" />
            </button>
          </div>
        </div>
      )}
      {/* Drag Indicator */}
      {isDragging && (
        <div className="absolute inset-0 bg-violet-400/20 border-2 border-dashed border-violet-400 rounded-2xl flex items-center justify-center">
          <div className="bg-violet-500 text-white px-3 py-1 rounded-lg text-xs font-medium">
            Moving...
          </div>
        </div>
      )}
    </div>
  );
};

export default CandidateCard;